/*
  ml-express-membership — Express Membership (design_handoff_express §2, §6, §7, §11).

  Add and go. Every plan card (and every sticky-bar button) is a POST form to /cart/add with
  id, quantity=1, selling_plan and return_to=/checkout, so it works without JavaScript. With it:
    1. busy state (X5·A): the tapped card spins, the others dim, a status line reads
       "TAKING YOU TO SECURE CHECKOUT…";
    2. fetch('/cart/add.js') with the form's own FormData — the same path as every other add, so
       monthlees-attribution.js has already refreshed the form's attributes[_attr_*] inputs (its
       capture-phase submit listener runs first) and re-syncs cart attributes, and
       monthlees-analytics.js still fires membership_add_to_cart;
    3. await MlMembershipQty.normalize(): one membership line at quantity 1 (the newest — the one
       just added — wins; product lines are never touched);
    4. await MlCheckoutGuard.check('express') (MON-457) and respect its answer;
    5. location.assign('/checkout').
  Any error resets the busy state and shows "That didn't go through. Try again." on the card; a
  10 s timeout resets it too, and so does coming back from checkout (bfcache pageshow).

  Also: the I1 "what's included" toggle, the page close button, the X4 modal (openers are any
  [data-ml-express="paid|trial"]) and the ml_express_* PostHog events via MonthleesAnalytics.track.
*/
(function () {
  'use strict';
  if (window.MlExpressMembership) return;

  var BUSY_TIMEOUT_MS = 10000;
  var ERROR_TEXT = 'That didn’t go through. Try again.';
  var STATUS_TEXT = 'TAKING YOU TO SECURE CHECKOUT…';
  var BUSY_CTA = 'Taking you to checkout…';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function track(name, props) {
    try {
      if (window.MonthleesAnalytics && typeof window.MonthleesAnalytics.track === 'function') {
        window.MonthleesAnalytics.track(name, props || {});
      } else if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(name, props || {});
      }
    } catch (e) { /* never break the page */ }
  }

  function source() {
    try {
      var rec = JSON.parse(window.localStorage.getItem('monthlees_attr_v1') || 'null') || {};
      return rec.utm_source || rec.last_utm_source || rec.first_utm_source || rec.source || '';
    } catch (e) { return ''; }
  }

  function rootOf(el) { return el && el.closest ? el.closest('[data-ml-xm]') : null; }
  function scopeOf(el) {
    return (el && el.closest && (el.closest('[data-ml-xm-page]') || el.closest('[data-ml-xm-offer-view]') || el.closest('[data-ml-xm-modal]'))) || document;
  }
  function ctx(root) {
    return {
      concept: root ? root.getAttribute('data-concept') : '',
      offer: root ? root.getAttribute('data-offer') : '',
      surface: root ? root.getAttribute('data-surface') : ''
    };
  }

  /* ---- add and go ------------------------------------------------------------------------ */
  var busy = null;

  function setBusy(form) {
    var scope = scopeOf(form);
    var root = scope.querySelector ? scope.querySelector('[data-ml-xm]') : null;
    if (root) root.classList.add('is-redirecting');
    var all = scope.querySelectorAll('[data-ml-xm-form]');
    Array.prototype.forEach.call(all, function (f) {
      if (f === form) {
        f.classList.add('is-busy');
      } else if (f.getAttribute('data-offer') === form.getAttribute('data-offer')) {
        f.classList.add('is-dim');
      }
      var btn = f.querySelector('button[type="submit"]');
      if (btn) btn.setAttribute('aria-disabled', 'true');
    });
    var cta = form.querySelector('[data-ml-xm-cta]');
    if (cta) {
      cta.setAttribute('data-label', cta.textContent);
      cta.textContent = BUSY_CTA;
    }
    var err = form.querySelector('[data-ml-xm-error]');
    if (err) { err.hidden = true; err.textContent = ''; }
    var status = scope.querySelector('[data-ml-xm-status]');
    if (status) status.textContent = STATUS_TEXT;
    var timer = setTimeout(function () { clearBusy(); }, BUSY_TIMEOUT_MS);
    busy = { form: form, scope: scope, timer: timer };
  }

  function clearBusy(message) {
    if (!busy) return;
    var b = busy;
    busy = null;
    clearTimeout(b.timer);
    var root = b.scope.querySelector ? b.scope.querySelector('[data-ml-xm]') : null;
    if (root) root.classList.remove('is-redirecting');
    Array.prototype.forEach.call(b.scope.querySelectorAll('[data-ml-xm-form]'), function (f) {
      f.classList.remove('is-busy', 'is-dim');
      var btn = f.querySelector('button[type="submit"]');
      if (btn) btn.removeAttribute('aria-disabled');
      var cta = f.querySelector('[data-ml-xm-cta]');
      if (cta && cta.hasAttribute('data-label')) {
        cta.textContent = cta.getAttribute('data-label');
        cta.removeAttribute('data-label');
      }
    });
    var status = b.scope.querySelector('[data-ml-xm-status]');
    if (status) status.textContent = '';
    if (message) {
      var err = b.form.querySelector('[data-ml-xm-error]');
      if (!err) {
        var bar = b.form.closest('[data-ml-xm-bar]');
        err = bar ? bar.querySelector('[data-ml-xm-bar-error]') : null;
      }
      if (err) { err.textContent = message; err.hidden = false; }
    }
  }

  function includesOpened(form) {
    var scope = scopeOf(form);
    var inc = scope.querySelector ? scope.querySelector('[data-ml-xm-inc]') : null;
    return !!(inc && !inc.hidden);
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('[data-ml-xm-form]')) return;
    if (typeof window.fetch !== 'function' || typeof window.FormData !== 'function') return; // plain POST
    event.preventDefault();
    if (busy) return;

    var root = rootOf(form) || (scopeOf(form).querySelector && scopeOf(form).querySelector('[data-ml-xm]'));
    var c = ctx(root);
    var route = form.getAttribute('data-route');
    track('ml_express_select', {
      route: route, offer: c.offer, concept: c.concept, surface: c.surface,
      includes_opened: includesOpened(form)
    });
    setBusy(form);
    var mine = busy;

    var body = new FormData(form);
    body.delete('return_to');
    window.fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok) {
            var reason = (data && (data.description || data.message)) || ('HTTP ' + res.status);
            throw new Error(reason);
          }
          return data;
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('monthlees:cart-updated', { detail: { source: 'ml-express-membership' } }));
        return window.MlMembershipQty && typeof window.MlMembershipQty.normalize === 'function'
          ? window.MlMembershipQty.normalize()
          : null;
      })
      .then(function () {
        if (window.MlCheckoutGuard && typeof window.MlCheckoutGuard.check === 'function') {
          return window.MlCheckoutGuard.check('express');
        }
        return true;
      })
      .then(function (ok) {
        if (busy !== mine) return; // timed out or reset meanwhile
        if (ok === false) {
          clearBusy();
          return;
        }
        window.location.assign('/checkout');
      })
      .catch(function (err) {
        track('ml_express_error', { route: route, reason: String((err && err.message) || err).slice(0, 200) });
        if (busy === mine) clearBusy(ERROR_TEXT);
      });
  }

  /* ---- I1: what's included ----------------------------------------------------------------- */
  function onIncludesToggle(link, event) {
    var id = link.getAttribute('aria-controls');
    var panel = id ? document.getElementById(id) : null;
    if (!panel) return; // plain link to /pages/memberships
    event.preventDefault();
    var scope = scopeOf(link);
    var bar = scope.querySelector('[data-ml-xm-bar]');
    var open = link.getAttribute('aria-expanded') === 'true';
    if (open) {
      link.setAttribute('aria-expanded', 'false');
      link.textContent = link.getAttribute('data-label-closed') || link.textContent;
      panel.classList.remove('is-open');
      panel.hidden = true;
      if (bar) bar.hidden = true;
      return;
    }
    link.setAttribute('aria-expanded', 'true');
    link.textContent = link.getAttribute('data-label-open') || link.textContent;
    panel.hidden = false;
    if (bar) bar.hidden = false;
    // next frame, so the opacity/translate transition runs (no height animation)
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { panel.classList.add('is-open'); });
    });
    var h = panel.querySelector('[tabindex="-1"]');
    if (h) {
      try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
      h.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
    var c = ctx(rootOf(link) || scope.querySelector('[data-ml-xm]'));
    track('ml_express_includes_open', { treatment: 'expand', concept: c.concept, offer: c.offer });
  }

  /* ---- page close -------------------------------------------------------------------------- */
  function onPageClose(a, event) {
    var c = ctx(rootOf(a));
    track('ml_express_close', { method: 'x', concept: c.concept, offer: c.offer, surface: 'page' });
    var sameOrigin = false;
    try { sameOrigin = !!document.referrer && new URL(document.referrer).origin === window.location.origin; } catch (e) {}
    if (sameOrigin && window.history.length > 1) {
      event.preventDefault();
      window.history.back();
    }
  }

  /* ---- X4 modal ---------------------------------------------------------------------------- */
  var modal = null;
  var opener = null;

  function modalRoot() {
    if (!modal) return null;
    var view = modal.querySelector('[data-ml-xm-offer-view]:not([hidden]) [data-ml-xm]');
    return view;
  }

  function openModal(offer, from) {
    modal = modal || document.querySelector('[data-ml-xm-modal]');
    if (!modal || typeof modal.showModal !== 'function') return false;
    offer = offer === 'trial' ? 'trial' : 'paid';
    // No second free week (29 Sep): a returning member opening the free week gets the paid cards.
    if (offer === 'trial' && window.MlFreeWeek && window.MlFreeWeek.blocked()) offer = 'paid';
    Array.prototype.forEach.call(modal.querySelectorAll('[data-ml-xm-offer-view]'), function (v) {
      v.hidden = v.getAttribute('data-ml-xm-offer-view') !== offer;
    });
    var root = modalRoot();
    if (!root) return false;
    modal.setAttribute('aria-labelledby', root.getAttribute('data-heading-id') || '');
    opener = from || document.activeElement;
    document.documentElement.classList.add('ml-xm-lock');
    modal.showModal();
    var panel = modal.querySelector('[data-ml-xm-modal-panel]');
    if (panel) panel.scrollTop = 0;
    viewed(root);
    return true;
  }

  function closeModal(method) {
    if (!modal || !modal.open) return;
    var c = ctx(modalRoot());
    track('ml_express_close', { method: method, concept: 'modal', offer: c.offer, surface: 'modal' });
    modal.close();
  }

  function bindModal() {
    modal = document.querySelector('[data-ml-xm-modal]');
    if (!modal) return;
    modal.addEventListener('cancel', function (e) { e.preventDefault(); closeModal('esc'); });
    modal.addEventListener('close', function () {
      document.documentElement.classList.remove('ml-xm-lock');
      if (busy && modal.contains(busy.form)) clearBusy();
      if (opener && typeof opener.focus === 'function') {
        try { opener.focus({ preventScroll: true }); } catch (e) { opener.focus(); }
      }
      opener = null;
    });
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal('backdrop'); // the dialog box itself = outside the panel
    });
  }

  /* ---- views ------------------------------------------------------------------------------- */
  var seen = [];
  function viewed(root) {
    if (!root || seen.indexOf(root) !== -1) return;
    seen.push(root);
    var c = ctx(root);
    track('ml_express_view', {
      concept: c.concept, offer: c.offer, surface: c.surface,
      member_state: root.getAttribute('data-member-state') || 'guest',
      source: source()
    });
  }

  function watchPageViews() {
    var roots = document.querySelectorAll('[data-ml-xm][data-surface="page"]');
    if (!roots.length) return;
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(roots, viewed);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { viewed(en.target); io.unobserve(en.target); }
      });
    }, { threshold: 0.25 });
    Array.prototype.forEach.call(roots, function (r) { io.observe(r); });
  }

  /* ---- wiring ------------------------------------------------------------------------------ */
  document.addEventListener('submit', onSubmit);
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var inc = t.closest('[data-ml-xm-inc-toggle]');
    if (inc) { onIncludesToggle(inc, e); return; }
    var esc = t.closest('[data-ml-xm-escape]');
    if (esc) {
      var c0 = ctx(rootOf(esc));
      track('ml_express_escape', { concept: c0.concept, offer: c0.offer, surface: c0.surface });
      return;
    }
    var close = t.closest('[data-ml-xm-close]');
    if (close) { onPageClose(close, e); return; }
    var mclose = t.closest('[data-ml-xm-modal-close]');
    if (mclose) { closeModal('x'); return; }
    var op = t.closest('[data-ml-express]');
    if (op && !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      if (openModal(op.getAttribute('data-ml-express'), op)) e.preventDefault();
    }
  });
  // Back from checkout restores this page from the bfcache with the busy state still on.
  window.addEventListener('pageshow', function (e) { if (e.persisted) clearBusy(); });

  function init() {
    bindModal();
    watchPageViews();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.MlExpressMembership = { open: openModal, close: closeModal };
})();
